import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getApiUrl } from "../utils/apiConfig";
import AdminTimeTracking from "./AdminTimeTracking";

const Dashboard = ({ user }) => {
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("projects");
  const [showErrorReport, setShowErrorReport] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [sendingReport, setSendingReport] = useState(false);
  const [reportSuccess, setReportSuccess] = useState(false);
  const [userFilter, setUserFilter] = useState("");

  // Email configuration state
  const [emailConfig, setEmailConfig] = useState({
    smtp_host: "smtp.gmail.com",
    smtp_port: 587,
    smtp_user: "",
    smtp_pass: "",
    admin_email: "",
    frontend_url: "http://localhost:3000",
  });
  const [emailConfigLoading, setEmailConfigLoading] = useState(false);
  const [emailConfigError, setEmailConfigError] = useState("");
  const [emailConfigSuccess, setEmailConfigSuccess] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [testingEmail, setTestingEmail] = useState(false);

  // Email templates state
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateError, setTemplateError] = useState("");
  const [templateSuccess, setTemplateSuccess] = useState("");
  const [templateVariables, setTemplateVariables] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [downloading, setDownloading] = useState({});

  // Project creation state
  const [newProject, setNewProject] = useState({
    name: "",
    source_language: "",
    target_language: "",
    video_path: "",
    source_subtitle_path: "",
    cps_limit: 20,
    cpl_limit: 42,
  });
  const [uploadedFiles, setUploadedFiles] = useState({
    video: null,
    subtitle: null,
  });

  // NAS file selection state
  const [nasFiles, setNasFiles] = useState({ videos: [], subtitles: [] });
  const [selectedNasFiles, setSelectedNasFiles] = useState({
    video: "",
    subtitle: "",
  });
  const [loadingNasFiles, setLoadingNasFiles] = useState(false);
  const [nasConnectionStatus, setNasConnectionStatus] = useState(null);
  const [useNasFiles, setUseNasFiles] = useState(true);
  const [uploading, setUploading] = useState(false);

  // File Explorer state
  const [currentDirectory, setCurrentDirectory] = useState("/");
  const [directoryHistory, setDirectoryHistory] = useState(["/"]);
  const [explorerFiles, setExplorerFiles] = useState([]);
  const [loadingExplorer, setLoadingExplorer] = useState(false);
  const [selectedExplorerFiles, setSelectedExplorerFiles] = useState({
    video: null,
    subtitle: null,
  });
  const [showExplorerModal, setShowExplorerModal] = useState(false);

  // Debug: Log when modal state changes
  useEffect(() => {
    console.log(
      "🔍 Dashboard showExplorerModal state changed:",
      showExplorerModal
    );
  }, [showExplorerModal]);

  // Assignment state
  const [assignment, setAssignment] = useState({
    project_id: "",
    user_id: "",
    role: "translator",
    cps_limit: 20,
    cpl_limit: 42,
  });

  // User creation state
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    password: "",
    user_type: "translator",
  });
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({
    show: false,
    user: null,
  });
  const [deletingUser, setDeletingUser] = useState(false);

  // Password generator functions
  const generateSecurePassword = () => {
    const lowercase = "abcdefghijklmnopqrstuvwxyz";
    const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const numbers = "0123456789";
    const symbols = "!@#$%^&*()_+-=[]{}|;:,.<>?";

    const allChars = lowercase + uppercase + numbers + symbols;
    let password = "";

    // Ensure at least one character from each category
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];

    // Fill the rest randomly (total length 12)
    for (let i = 4; i < 12; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }

    // Shuffle the password
    return password
      .split("")
      .sort(() => Math.random() - 0.5)
      .join("");
  };

  const handleGeneratePassword = () => {
    const newPassword = generateSecurePassword();
    setNewUser({ ...newUser, password: newPassword });
    setPasswordCopied(false);
  };

  const handleCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(newUser.password);
      setPasswordCopied(true);
      setTimeout(() => setPasswordCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy password:", err);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = newUser.password;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setPasswordCopied(true);
      setTimeout(() => setPasswordCopied(false), 2000);
    }
  };

  const showDeleteConfirm = (listedUser) => {
    setDeleteConfirm({ show: true, user: listedUser });
  };

  const cancelUserDelete = () => {
    setDeleteConfirm({ show: false, user: null });
  };

  const handleDeleteUser = async () => {
    if (!deleteConfirm.user) return;
    try {
      setDeletingUser(true);
      const deleteUrl = await getApiUrl(
        `/api/admin/users/${deleteConfirm.user.id}`
      );
      await axios.delete(deleteUrl);
      setUsers((prev) => prev.filter((u) => u.id !== deleteConfirm.user.id));
      setDeleteConfirm({ show: false, user: null });
    } catch (error) {
      console.error("Error deleting user:", error);
      alert(
        "Failed to delete user: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setDeletingUser(false);
    }
  };

  const getPasswordStrength = (password) => {
    if (!password) return "";

    let score = 0;
    let feedback = [];

    // Length check
    if (password.length >= 8) score += 1;
    else feedback.push("at least 8 characters");

    // Character variety checks
    if (/[a-z]/.test(password)) score += 1;
    else feedback.push("lowercase letters");

    if (/[A-Z]/.test(password)) score += 1;
    else feedback.push("uppercase letters");

    if (/[0-9]/.test(password)) score += 1;
    else feedback.push("numbers");

    if (/[^A-Za-z0-9]/.test(password)) score += 1;
    else feedback.push("special characters");

    // Strength levels
    if (score <= 2) return "Weak";
    if (score <= 3) return "Fair";
    if (score <= 4) return "Good";
    return "Strong";
  };

  // Project view state
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectEntries, setProjectEntries] = useState([]);
  const [projectViewLoading, setProjectViewLoading] = useState(false);

  // Project editing state
  const [editingProject, setEditingProject] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteProjectModal, setShowDeleteProjectModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [editingProjectData, setEditingProjectData] = useState({
    name: "",
    source_language: "",
    target_language: "",
    cps_limit: 20,
    cpl_limit: 42,
    video_path: "",
    source_subtitle_path: "",
  });

  // File upload state for editing
  const [editingUploadedFiles, setEditingUploadedFiles] = useState({
    video: null,
    subtitle: null,
  });

  // NAS file selection state for editing
  const [editingSelectedExplorerFiles, setEditingSelectedExplorerFiles] =
    useState({
      video: null,
      subtitle: null,
    });
  const [showEditingExplorerModal, setShowEditingExplorerModal] =
    useState(false);
  const [editingUseNasFiles, setEditingUseNasFiles] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
    if (activeTab === "email") {
      fetchEmailConfig();
    } else if (activeTab === "templates") {
      fetchEmailTemplates();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) {
        setError("No authentication token found");
        setLoading(false);
        return;
      }

      // Fetch different data based on user type
      if (user.user_type === "admin") {
        const [usersUrl, projectsUrl, notificationsUrl] = await Promise.all([
          getApiUrl("/api/admin/users"),
          getApiUrl("/api/admin/projects"),
          getApiUrl("/api/notifications"),
        ]);

        const [usersRes, projectsRes, notificationsRes] = await Promise.all([
          axios.get(usersUrl),
          axios.get(projectsUrl),
          axios.get(notificationsUrl),
        ]);

        setUsers(usersRes.data);
        setProjects(projectsRes.data);
        setNotifications(notificationsRes.data);
      } else {
        // Regular user - only fetch projects
        const apiUrl = await getApiUrl("/api/projects");
        const response = await axios.get(apiUrl);
        setProjects(response.data);
      }
    } catch (error) {
      setError("Failed to load data");
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartProject = async (projectId) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("No authentication token found");
        return;
      }

      const apiUrl = await getApiUrl(`/api/projects/${projectId}/start`);
      await axios.post(apiUrl);
      navigate(`/editor/${projectId}`);
    } catch (error) {
      setError("Failed to start project");
      console.error("Error starting project:", error);
    }
  };

  const handleSendErrorReport = async () => {
    if (!errorMessage.trim()) {
      setError("Please enter a message describing the issue");
      return;
    }

    try {
      setSendingReport(true);
      const apiUrl = await getApiUrl("/api/report-error");
      await axios.post(apiUrl, {
        message: errorMessage.trim(),
        user_name: user.name,
        user_email: user.email,
        user_type: user.user_type,
        project_name: null, // Dashboard doesn't have specific project context
        project_id: null,
      });

      setReportSuccess(true);
      setErrorMessage("");
      setTimeout(() => {
        setShowErrorReport(false);
        setReportSuccess(false);
      }, 2000);
    } catch (error) {
      setError("Failed to send error report");
      console.error("Error sending report:", error);
    } finally {
      setSendingReport(false);
    }
  };

  // NAS functions
  const testNasConnection = async () => {
    console.log("🔍 Dashboard testNasConnection called");
    try {
      setLoadingNasFiles(true);
      console.log("🔍 Loading NAS files...");
      const testUrl = await getApiUrl("/api/admin/nas-test");
      console.log("🔍 Test URL:", testUrl);
      const response = await axios.get(testUrl);
      console.log("🔍 NAS test response:", response.data);

      if (response.data.success) {
        console.log("✅ NAS connection successful, opening modal...");
        setNasConnectionStatus("connected");
        setError("");
        // Open file explorer modal after successful connection
        setShowExplorerModal(true);
        console.log("🔍 Modal should be open now");
        // Load root directory files
        await loadExplorerFiles("/");
      } else {
        console.log("❌ NAS connection failed");
        setNasConnectionStatus("failed");
        setError("Failed to connect to NAS");
      }
    } catch (error) {
      console.error("❌ NAS connection error:", error);
      setNasConnectionStatus("failed");
      setError(
        "Failed to connect to NAS: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setLoadingNasFiles(false);
    }
  };

  const loadNasFiles = async () => {
    try {
      setLoadingNasFiles(true);

      const videosUrl = await getApiUrl("/api/admin/nas-files?type=video");
      const videosResponse = await axios.get(videosUrl);

      const subtitlesUrl = await getApiUrl(
        "/api/admin/nas-files?type=subtitle"
      );
      const subtitlesResponse = await axios.get(subtitlesUrl);

      setNasFiles({
        videos: videosResponse.data.files || [],
        subtitles: subtitlesResponse.data.files || [],
      });

      setError("");
    } catch (error) {
      setError(
        "Failed to load NAS files: " +
          (error.response?.data?.message || error.message)
      );
      console.error("Error loading NAS files:", error);
    } finally {
      setLoadingNasFiles(false);
    }
  };

  const handleNasFileSelection = () => {
    if (!selectedNasFiles.video || !selectedNasFiles.subtitle) {
      setError("Please select both video and subtitle files from NAS");
      return;
    }

    setNewProject((prev) => ({
      ...prev,
      video_path: `/nas${selectedNasFiles.video}`,
      source_subtitle_path: `/nas${selectedNasFiles.subtitle}`,
      use_nas: true,
    }));

    setError("");
  };

  // File Explorer functions
  const loadExplorerFiles = async (directory = currentDirectory) => {
    console.log(
      "🔍 Dashboard loadExplorerFiles called with directory:",
      directory
    );
    try {
      setLoadingExplorer(true);
      const url = await getApiUrl(
        `/api/admin/nas-files?directory=${encodeURIComponent(directory)}`
      );
      console.log("🔍 Explorer files URL:", url);
      const response = await axios.get(url);
      console.log("🔍 Explorer files response:", response.data);

      setExplorerFiles(response.data.files || []);
      setCurrentDirectory(directory);
      setError("");
      console.log("✅ Explorer files loaded successfully");
    } catch (error) {
      console.error("❌ Error loading explorer files:", error);
      setError(
        "Failed to load directory: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setLoadingExplorer(false);
    }
  };

  const navigateToDirectory = (directory) => {
    setDirectoryHistory((prev) => [...prev, directory]);
    loadExplorerFiles(directory);
  };

  const navigateBack = () => {
    if (directoryHistory.length > 1) {
      const newHistory = directoryHistory.slice(0, -1);
      setDirectoryHistory(newHistory);
      const previousDirectory = newHistory[newHistory.length - 1];
      loadExplorerFiles(previousDirectory);
    }
  };

  const selectExplorerFile = (file) => {
    if (file.type === "video") {
      setSelectedExplorerFiles((prev) => ({ ...prev, video: file }));
    } else if (file.type === "subtitle") {
      setSelectedExplorerFiles((prev) => ({ ...prev, subtitle: file }));
    }
  };

  const handleExplorerFileSelection = () => {
    if (!selectedExplorerFiles.video || !selectedExplorerFiles.subtitle) {
      setError("Please select both video and subtitle files from the explorer");
      return;
    }

    // Update the new project form with selected explorer file paths
    setNewProject((prev) => ({
      ...prev,
      video_path: selectedExplorerFiles.video.path, // Remove /nas prefix
      source_subtitle_path: selectedExplorerFiles.subtitle.path, // Remove /nas prefix
      use_nas: true,
    }));

    // Close the modal and show success
    setShowExplorerModal(false);
    setError("");
  };

  // Admin functions
  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!uploadedFiles.video || !uploadedFiles.subtitle) {
      setError("Please select both video and subtitle files");
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("video", uploadedFiles.video);
      formData.append("subtitle", uploadedFiles.subtitle);

      const uploadUrl = await getApiUrl("/api/admin/upload");
      const response = await axios.post(uploadUrl, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setNewProject((prev) => ({
        ...prev,
        video_path: response.data.videoPath,
        source_subtitle_path: response.data.subtitlePath,
      }));

      setUploadedFiles({ video: null, subtitle: null });
      setError("");
    } catch (error) {
      setError("Failed to upload files");
      console.error("Error uploading files:", error);
    } finally {
      setUploading(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!newProject.video_path || !newProject.source_subtitle_path) {
      setError("Please upload video and subtitle files first");
      return;
    }

    try {
      const projectsUrl = await getApiUrl("/api/admin/projects");

      // Prepare project data with NAS flag
      const projectData = {
        ...newProject,
        use_nas: useNasFiles, // Add NAS flag
      };

      console.log("Creating project with data:", projectData);

      await axios.post(projectsUrl, projectData);

      // Reset form
      setNewProject({
        name: "",
        source_language: "",
        target_language: "",
        video_path: "",
        source_subtitle_path: "",
        cps_limit: 20,
        cpl_limit: 42,
      });

      // Reset NAS selection
      setSelectedExplorerFiles({
        video: null,
        subtitle: null,
      });

      fetchData();
      setError("");
      alert("✅ Project created successfully!");
    } catch (error) {
      setError(
        "Failed to create project: " +
          (error.response?.data?.message || error.message)
      );
      console.error("Error creating project:", error);
    }
  };

  const handleAssignProject = async (e) => {
    e.preventDefault();
    try {
      const selectedProject = projects.find(
        (p) => p.id === parseInt(assignment.project_id, 10)
      );
      if (
        selectedProject &&
        selectedProject.current_user_id != null &&
        selectedProject.current_user_id !== undefined
      ) {
        const currentUser = users.find(
          (u) => u.id === selectedProject.current_user_id
        );
        const currentUserName = currentUser ? currentUser.name : "Unknown User";
        alert(
          `⚠️ Project "${selectedProject.name}" is already assigned to ${currentUserName} (${selectedProject.status}). Cannot double-assign.`
        );
        return;
      }

      const requestData = {
        userId: parseInt(assignment.user_id, 10),
        userType: assignment.role,
      };

      await axios.post(
        `/api/admin/projects/${parseInt(assignment.project_id, 10)}/assign`,
        requestData
      );

      const assignedUser = users.find(
        (u) => u.id === parseInt(assignment.user_id, 10)
      );
      const assignedUserName = assignedUser
        ? assignedUser.name
        : "Unknown User";

      alert(
        `✅ Project "${selectedProject.name}" has been successfully assigned to ${assignedUserName} (${assignment.role})`
      );

      setAssignment({
        project_id: "",
        user_id: "",
        role: "translator",
        cps_limit: 20,
        cpl_limit: 42,
      });
      fetchData();
      setError("");
    } catch (error) {
      if (error.response?.data?.message) {
        setError(`Failed to assign project: ${error.response.data.message}`);
      } else {
        setError("Failed to assign project");
      }
      console.error("Error assigning project:", error);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUser.name || !newUser.email || !newUser.password) {
      setError("All fields are required for user creation");
      return;
    }

    try {
      setCreatingUser(true);
      const registerUrl = await getApiUrl("/api/auth/register");
      await axios.post(registerUrl, newUser);
      setNewUser({
        name: "",
        email: "",
        password: "",
        user_type: "translator",
      });
      fetchData();
      setError("");
      alert("User created successfully!");
    } catch (error) {
      setError(
        "Failed to create user: " +
          (error.response?.data?.message || error.message)
      );
      console.error("Error creating user:", error);
    } finally {
      setCreatingUser(false);
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await axios.put(`/api/notifications/${notificationId}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, is_read: 1 } : n))
      );
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const downloadSRT = async (projectId, projectName, translated = false) => {
    try {
      setDownloading((prev) => ({ ...prev, [projectId]: true }));

      const endpoint = translated
        ? `/api/projects/${projectId}/download-translated`
        : `/api/projects/${projectId}/download`;

      const response = await axios.get(endpoint, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const filename = translated
        ? `${projectName}_translated.srt`
        : `${projectName}_original.srt`;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setError(
        `Failed to download ${translated ? "translated" : "original"} SRT file`
      );
      console.error("Error downloading SRT:", error);
    } finally {
      setDownloading((prev) => ({ ...prev, [projectId]: false }));
    }
  };

  // Email configuration functions
  const fetchEmailConfig = async () => {
    try {
      setEmailConfigLoading(true);
      const configUrl = await getApiUrl("/api/admin/email-config");
      const response = await axios.get(configUrl);
      setEmailConfig(response.data);
      setEmailConfigError("");
    } catch (error) {
      console.error("Error fetching email config:", error);
      setEmailConfigError("Failed to load email configuration");
    } finally {
      setEmailConfigLoading(false);
    }
  };

  const saveEmailConfig = async (e) => {
    e.preventDefault();
    if (
      !emailConfig.smtp_host ||
      !emailConfig.smtp_port ||
      !emailConfig.smtp_user ||
      !emailConfig.admin_email
    ) {
      setEmailConfigError("Please fill in all required fields");
      return;
    }

    try {
      setEmailConfigLoading(true);
      setEmailConfigError("");
      setEmailConfigSuccess("");

      const configUrl = await getApiUrl("/api/admin/email-config");
      await axios.put(configUrl, emailConfig);

      setEmailConfigSuccess("Email configuration saved successfully!");
      setTimeout(() => setEmailConfigSuccess(""), 5000);
    } catch (error) {
      console.error("Error saving email config:", error);
      setEmailConfigError(
        "Failed to save email configuration: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setEmailConfigLoading(false);
    }
  };

  const testEmailConfig = async () => {
    if (!testEmail) {
      setEmailConfigError("Please enter a test email address");
      return;
    }

    if (
      !emailConfig.smtp_host ||
      !emailConfig.smtp_port ||
      !emailConfig.smtp_user ||
      !emailConfig.smtp_pass
    ) {
      setEmailConfigError(
        "Please fill in all email configuration fields before testing"
      );
      return;
    }

    try {
      setTestingEmail(true);
      setEmailConfigError("");
      setEmailConfigSuccess("");

      const testUrl = await getApiUrl("/api/admin/email-config/test");
      const response = await axios.post(testUrl, {
        ...emailConfig,
        test_email: testEmail,
      });

      setEmailConfigSuccess(
        `Test email sent successfully to ${testEmail}! Message ID: ${response.data.messageId}`
      );
      setTimeout(() => setEmailConfigSuccess(""), 10000);
    } catch (error) {
      console.error("Error testing email config:", error);

      let errorMessage = "Email test failed";
      if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
        if (error.response.data.error.details) {
          errorMessage += ": " + error.response.data.error.details;
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else {
        errorMessage += ": " + error.message;
      }

      setEmailConfigError(errorMessage);
    } finally {
      setTestingEmail(false);
    }
  };

  // Email template functions
  const fetchEmailTemplates = async () => {
    try {
      setTemplateLoading(true);
      const templatesUrl = await getApiUrl("/api/admin/email-templates");
      const response = await axios.get(templatesUrl);
      setEmailTemplates(response.data);
      setTemplateError("");
    } catch (error) {
      console.error("Error fetching email templates:", error);
      setTemplateError("Failed to load email templates");
    } finally {
      setTemplateLoading(false);
    }
  };

  const selectTemplate = async (templateType) => {
    try {
      setTemplateLoading(true);
      const templateUrl = await getApiUrl(
        `/api/admin/email-templates/${templateType}`
      );
      const response = await axios.get(templateUrl);
      setSelectedTemplate(response.data);

      const variablesUrl = await getApiUrl(
        `/api/admin/email-templates/variables/${templateType}`
      );
      const variablesResponse = await axios.get(variablesUrl);
      setTemplateVariables(variablesResponse.data);

      setTemplateError("");
    } catch (error) {
      console.error("Error fetching template:", error);
      setTemplateError("Failed to load template");
    } finally {
      setTemplateLoading(false);
    }
  };

  const saveTemplate = async () => {
    if (!selectedTemplate) return;

    try {
      setTemplateLoading(true);
      setTemplateError("");
      setTemplateSuccess("");

      const templateUrl = await getApiUrl(
        `/api/admin/email-templates/${selectedTemplate.template_type}`
      );
      await axios.put(templateUrl, {
        subject: selectedTemplate.subject,
        html_content: selectedTemplate.html_content,
        text_content: selectedTemplate.text_content,
      });

      setTemplateSuccess("Template saved successfully!");
      setTimeout(() => setTemplateSuccess(""), 5000);
    } catch (error) {
      console.error("Error saving template:", error);
      setTemplateError(
        "Failed to save template: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setTemplateLoading(false);
    }
  };

  const previewTemplate = async () => {
    if (!selectedTemplate) return;

    try {
      setTemplateLoading(true);
      const previewUrl = await getApiUrl(
        `/api/admin/email-templates/${selectedTemplate.template_type}/preview`
      );
      const response = await axios.post(previewUrl, {
        subject: selectedTemplate.subject,
        html_content: selectedTemplate.html_content,
        text_content: selectedTemplate.text_content,
      });

      setPreviewData(response.data);
      setTemplateError("");
    } catch (error) {
      console.error("Error previewing template:", error);
      setTemplateError("Failed to preview template");
    } finally {
      setTemplateLoading(false);
    }
  };

  const testTemplate = async () => {
    if (!selectedTemplate || !testEmail) return;

    try {
      setTemplateLoading(true);
      setTemplateError("");
      setTemplateSuccess("");

      const testUrl = await getApiUrl(
        `/api/admin/email-templates/${selectedTemplate.template_type}/test`
      );
      await axios.post(testUrl, {
        test_email: testEmail,
        subject: selectedTemplate.subject,
        html_content: selectedTemplate.html_content,
        text_content: selectedTemplate.text_content,
      });

      setTemplateSuccess(`Test email sent successfully to ${testEmail}!`);
      setTimeout(() => setTemplateSuccess(""), 10000);
    } catch (error) {
      console.error("Error testing template:", error);
      setTemplateError(
        "Failed to send test email: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setTemplateLoading(false);
    }
  };

  // Project view functions
  const viewProject = async (projectId) => {
    try {
      setProjectViewLoading(true);
      setSelectedProject(null);
      setProjectEntries([]);

      // Find the project in the projects list
      const project = projects.find((p) => p.id === projectId);
      if (!project) {
        setError("Project not found");
        return;
      }

      setSelectedProject(project);

      // Fetch project details with entries
      const projectUrl = await getApiUrl(`/api/projects/${projectId}`);
      const projectResponse = await axios.get(projectUrl);
      setProjectEntries(projectResponse.data.entries || []);

      setActiveTab("project-view");
    } catch (error) {
      setError("Failed to load project details");
      console.error("Error loading project:", error);
    } finally {
      setProjectViewLoading(false);
    }
  };

  const getStatusClass = (status) => {
    const statusMap = {
      pending: "status-pending",
      translating: "status-translating",
      translated: "status-translated",
      proofreading: "status-proofreading",
      completed: "status-completed",
    };
    return statusMap[status] || "status-pending";
  };

  const getStatusText = (status) => {
    const statusMap = {
      pending: "Pending",
      translating: "In Translation",
      translated: "Translated",
      proofreading: "In Proofreading",
      completed: "Completed",
    };
    return statusMap[status] || status;
  };

  // Project editing functions
  const handleEditProject = (project) => {
    setEditingProject(project);
    setEditingProjectData({
      name: project.name,
      source_language: project.source_language,
      target_language: project.target_language,
      cps_limit: project.cps_limit,
      cpl_limit: project.cpl_limit,
      video_path: project.video_path || "",
      source_subtitle_path: project.source_subtitle_path || "",
    });

    // Reset file states
    setEditingUploadedFiles({ video: null, subtitle: null });
    setEditingSelectedExplorerFiles({ video: null, subtitle: null });
    setEditingUseNasFiles(true);

    setShowEditModal(true);
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    if (!editingProject) return;

    try {
      const projectUrl = await getApiUrl(
        `/api/admin/projects/${editingProject.id}`
      );
      await axios.put(projectUrl, editingProjectData);

      // Update the project in the local state
      setProjects((prev) =>
        prev.map((p) =>
          p.id === editingProject.id ? { ...p, ...editingProjectData } : p
        )
      );

      setShowEditModal(false);
      setEditingProject(null);
      setError("");
      alert("✅ Project updated successfully!");
    } catch (error) {
      setError(
        "Failed to update project: " +
          (error.response?.data?.message || error.message)
      );
      console.error("Error updating project:", error);
    }
  };

  const handleDeleteProject = (project) => {
    setProjectToDelete(project);
    setShowDeleteProjectModal(true);
  };

  const confirmDeleteProject = async () => {
    if (!projectToDelete) return;

    try {
      const projectUrl = await getApiUrl(
        `/api/admin/projects/${projectToDelete.id}`
      );
      await axios.delete(projectUrl);

      // Remove the project from the local state
      setProjects((prev) => prev.filter((p) => p.id !== projectToDelete.id));

      setShowDeleteProjectModal(false);
      setProjectToDelete(null);
      setError("");
      alert("✅ Project deleted successfully!");
    } catch (error) {
      setError(
        "Failed to delete project: " +
          (error.response?.data?.message || error.message)
      );
      console.error("Error deleting project:", error);
    }
  };

  const cancelEdit = () => {
    setShowEditModal(false);
    setEditingProject(null);
    setEditingProjectData({
      name: "",
      source_language: "",
      target_language: "",
      cps_limit: 20,
      cpl_limit: 42,
    });
  };

  const cancelDelete = () => {
    setShowDeleteProjectModal(false);
    setProjectToDelete(null);
  };

  // File upload functions for editing
  const handleEditingFileUpload = async (e) => {
    e.preventDefault();
    if (!editingUploadedFiles.video || !editingUploadedFiles.subtitle) {
      setError("Please select both video and subtitle files");
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("video", editingUploadedFiles.video);
      formData.append("subtitle", editingUploadedFiles.subtitle);

      const uploadUrl = await getApiUrl("/api/admin/upload");
      const response = await axios.post(uploadUrl, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setEditingProjectData((prev) => ({
        ...prev,
        video_path: response.data.videoPath,
        source_subtitle_path: response.data.subtitlePath,
      }));

      setEditingUploadedFiles({ video: null, subtitle: null });
      setError("");
      alert("✅ Files uploaded successfully!");
    } catch (error) {
      setError("Failed to upload files");
      console.error("Error uploading files:", error);
    } finally {
      setUploading(false);
    }
  };

  const handleEditingNasFileSelection = () => {
    if (
      !editingSelectedExplorerFiles.video ||
      !editingSelectedExplorerFiles.subtitle
    ) {
      setError("Please select both video and subtitle files from the explorer");
      return;
    }

    setEditingProjectData((prev) => ({
      ...prev,
      video_path: editingSelectedExplorerFiles.video.path,
      source_subtitle_path: editingSelectedExplorerFiles.subtitle.path,
    }));

    setShowEditingExplorerModal(false);
    setError("");
    alert("✅ Files selected from NAS successfully!");
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div style={{ textAlign: "center", padding: "2rem" }}>Loading...</div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1 className="dashboard-title">Welcome, {user.name}!</h1>

        {/* Admin Tabs - Only show for admin users, positioned below welcome message */}
        {user.user_type === "admin" && (
          <div className="admin-tabs" style={{ marginTop: "1rem" }}>
            <button
              className={`tab-button ${
                activeTab === "projects" ? "active" : ""
              }`}
              onClick={() => setActiveTab("projects")}
            >
              Projects
            </button>
            <button
              className={`tab-button ${activeTab === "users" ? "active" : ""}`}
              onClick={() => setActiveTab("users")}
            >
              Users
            </button>
            <button
              className={`tab-button ${
                activeTab === "notifications" ? "active" : ""
              }`}
              onClick={() => setActiveTab("notifications")}
            >
              Notifications ({notifications.filter((n) => !n.is_read).length})
            </button>
            <button
              className={`tab-button ${activeTab === "email" ? "active" : ""}`}
              onClick={() => setActiveTab("email")}
            >
              Email Config
            </button>
            <button
              className={`tab-button ${
                activeTab === "templates" ? "active" : ""
              }`}
              onClick={() => setActiveTab("templates")}
            >
              Email Templates
            </button>
            <button
              className={`tab-button ${
                activeTab === "time-tracking" ? "active" : ""
              }`}
              onClick={() => setActiveTab("time-tracking")}
            >
              Time Tracking
            </button>
          </div>
        )}
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Admin Tab Content */}
      {user.user_type === "admin" ? (
        <>
          {activeTab === "projects" && (
            <div className="admin-section">
              <h2>File Selection</h2>

              {/* File Source Selection */}
              {/* <div className="form-group" style={{ marginBottom: "1rem" }}>
                <label>File Source:</label>
                <div
                  style={{ display: "flex", gap: "1rem", marginTop: "0.5rem" }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <input
                      type="radio"
                      name="fileSource"
                      checked={!useNasFiles}
                      onChange={() => setUseNasFiles(false)}
                    />
                    Upload Files
                  </label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <input
                      type="radio"
                      name="fileSource"
                      checked={useNasFiles}
                      onChange={() => setUseNasFiles(true)}
                    />
                    Select from NAS
                  </label>
                </div>
              </div> */}

              {/* NAS File Selection */}
              {useNasFiles && (
                <div
                  className="nas-file-selection"
                  style={{
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    padding: "1rem",
                    marginBottom: "1rem",
                    backgroundColor: "#f9f9f9",
                  }}
                >
                  <div style={{ display: "flex", gap: "1rem" }}>
                    <h3>📁 File Selection</h3>
                    <button
                      type="button"
                      onClick={testNasConnection}
                      className="btn btn-primary"
                      disabled={loadingNasFiles}
                      style={{ marginRight: "1rem" }}
                    >
                      {loadingNasFiles ? "Connecting..." : "Open File Explorer"}
                    </button>
                  </div>

                  {/* NAS Connection Status */}

                  {/* Selected Files Display */}
                  {selectedExplorerFiles.video &&
                    selectedExplorerFiles.subtitle && (
                      <div
                        style={{
                          padding: "1rem",
                          backgroundColor: "#d4edda",
                          border: "1px solid #c3e6cb",
                          borderRadius: "4px",
                          marginTop: "1rem",
                        }}
                      >
                        <h4
                          style={{ color: "#155724", margin: "0 0 0.5rem 0" }}
                        >
                          ✅ Files Selected from NAS
                        </h4>
                        <div style={{ fontSize: "0.9rem", color: "#155724" }}>
                          <div>
                            🎬 Video: {selectedExplorerFiles.video.name}
                          </div>
                          <div>
                            📝 Subtitle: {selectedExplorerFiles.subtitle.name}
                          </div>
                        </div>
                      </div>
                    )}
                </div>
              )}

              {/* File Upload (Original) */}
              {!useNasFiles && (
                <form onSubmit={handleFileUpload} className="admin-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Video File (MP4)</label>
                      <input
                        type="file"
                        accept="video/mp4"
                        onChange={(e) =>
                          setUploadedFiles({
                            ...uploadedFiles,
                            video: e.target.files[0],
                          })
                        }
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Subtitle File (SRT)</label>
                      <input
                        type="file"
                        accept=".srt"
                        onChange={(e) =>
                          setUploadedFiles({
                            ...uploadedFiles,
                            subtitle: e.target.files[0],
                          })
                        }
                        required
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={uploading}
                  >
                    {uploading ? "Uploading..." : "Upload Files"}
                  </button>
                </form>
              )}

              <h2>Create New Project</h2>
              <form onSubmit={handleCreateProject} className="admin-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Project Name</label>
                    <input
                      type="text"
                      value={newProject.name}
                      onChange={(e) =>
                        setNewProject({ ...newProject, name: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Source Language</label>
                    <input
                      type="text"
                      value={newProject.source_language}
                      onChange={(e) =>
                        setNewProject({
                          ...newProject,
                          source_language: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Target Language</label>
                    <input
                      type="text"
                      value={newProject.target_language}
                      onChange={(e) =>
                        setNewProject({
                          ...newProject,
                          target_language: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>CPS Limit (Characters Per Second)</label>
                    <input
                      type="number"
                      value={newProject.cps_limit}
                      onChange={(e) =>
                        setNewProject({
                          ...newProject,
                          cps_limit: parseInt(e.target.value) || 20,
                        })
                      }
                      min="1"
                      max="50"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>CPL Limit (Characters Per Line)</label>
                    <input
                      type="number"
                      value={newProject.cpl_limit}
                      onChange={(e) =>
                        setNewProject({
                          ...newProject,
                          cpl_limit: parseInt(e.target.value) || 42,
                        })
                      }
                      min="1"
                      max="100"
                      required
                    />
                  </div>
                </div>

                <button type="submit" className="btn btn-primary">
                  Create Project
                </button>
              </form>

              <h2>Assign Project to User</h2>
              <form onSubmit={handleAssignProject} className="admin-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Project</label>
                    <select
                      value={assignment.project_id}
                      onChange={(e) =>
                        setAssignment({
                          ...assignment,
                          project_id: e.target.value,
                        })
                      }
                      required
                    >
                      <option value="">Select Project</option>
                      {projects
                        .filter((p) => {
                          const status = p.status?.toLowerCase();
                          const isUnassigned =
                            p.current_user_id == null ||
                            p.current_user_id === undefined ||
                            p.current_user_id === "";

                          // Include pending projects
                          if (status === "pending") return true;
                          // Include translated projects
                          if (status === "translated") return true;
                          // Include proofreading projects that are not assigned
                          if (status === "proofreading" && isUnassigned)
                            return true;
                          return false;
                        })
                        .map((project) => {
                          const isAssigned =
                            project.current_user_id != null &&
                            project.current_user_id !== undefined;
                          const currentUser = isAssigned
                            ? users.find(
                                (u) => u.id === project.current_user_id
                              )
                            : null;
                          const currentUserName = currentUser
                            ? currentUser.name
                            : "Unknown User";
                          return (
                            <option key={project.id} value={project.id}>
                              {project.name}{" "}
                              {isAssigned
                                ? `(Currently: ${currentUserName})`
                                : "(Unassigned)"}
                            </option>
                          );
                        })}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>User</label>
                    <select
                      value={assignment.user_id}
                      onChange={(e) =>
                        setAssignment({
                          ...assignment,
                          user_id: e.target.value,
                        })
                      }
                      required
                    >
                      <option value="">Select User</option>
                      {users
                        .filter((u) => u.is_active)
                        .map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.name} ({user.user_type})
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Role</label>
                    <select
                      value={assignment.role}
                      onChange={(e) =>
                        setAssignment({ ...assignment, role: e.target.value })
                      }
                      required
                    >
                      <option value="translator">Translator</option>
                      <option value="proofreader">Proofreader</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>CPS Limit</label>
                    <input
                      type="number"
                      value={assignment.cps_limit}
                      onChange={(e) =>
                        setAssignment({
                          ...assignment,
                          cps_limit: parseInt(e.target.value),
                        })
                      }
                      min="1"
                      max="50"
                    />
                  </div>
                  <div className="form-group">
                    <label>CPL Limit</label>
                    <input
                      type="number"
                      value={assignment.cpl_limit}
                      onChange={(e) =>
                        setAssignment({
                          ...assignment,
                          cpl_limit: parseInt(e.target.value),
                        })
                      }
                      min="1"
                      max="100"
                    />
                  </div>
                </div>
                <button type="submit" className="btn btn-primary">
                  Assign Project
                </button>
              </form>

              <h2>All Projects</h2>
              <div className="projects-table">
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Languages</th>
                      <th>Status</th>
                      <th>Current User</th>
                      <th>CPS/CPL Limits</th>
                      <th>Assigned By</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((project) => (
                      <tr key={project.id}>
                        <td>{project.id}</td>
                        <td>{project.name}</td>
                        <td>
                          {project.source_language} → {project.target_language}
                        </td>
                        <td>
                          <span
                            className={`status-badge status-${project.status}`}
                          >
                            {project.status}
                          </span>
                        </td>
                        <td>{project.current_user_name || "Unassigned"}</td>
                        <td>
                          {project.cps_limit}/{project.cpl_limit}
                        </td>
                        <td>{project.assigned_by_name || "-"}</td>
                        <td>
                          {new Date(project.created_at).toLocaleDateString()}
                        </td>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              gap: "0.5rem",
                              flexWrap: "wrap",
                            }}
                          >
                            <button
                              className="btn btn-sm btn-outline-info"
                              onClick={() => viewProject(project.id)}
                              title="View project details and translations"
                            >
                              👁️ View
                            </button>
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() =>
                                downloadSRT(project.id, project.name, true)
                              }
                              disabled={downloading[project.id]}
                              title="Download translated SRT file"
                            >
                              {downloading[project.id] ? "⏳" : "📄"} Download
                              Translated
                            </button>
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() =>
                                downloadSRT(project.id, project.name, false)
                              }
                              disabled={downloading[project.id]}
                              title="Download original SRT file"
                            >
                              {downloading[project.id] ? "⏳" : "📝"} Download
                              Original
                            </button>
                            <button
                              className="btn btn-sm btn-outline-warning"
                              onClick={() => handleEditProject(project)}
                              title="Edit project details"
                            >
                              ✏️ Edit
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDeleteProject(project)}
                              title="Delete project"
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === "users" && (
            <div className="admin-section">
              <h2>Create New User</h2>
              <form onSubmit={handleCreateUser} className="admin-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Full Name</label>
                    <input
                      type="text"
                      value={newUser.name}
                      onChange={(e) =>
                        setNewUser({ ...newUser, name: e.target.value })
                      }
                      placeholder="Enter user's full name"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Email Address</label>
                    <input
                      type="email"
                      value={newUser.email}
                      onChange={(e) =>
                        setNewUser({ ...newUser, email: e.target.value })
                      }
                      placeholder="Enter user's email"
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Password</label>
                    <div className="password-input-group">
                      <input
                        type="password"
                        value={newUser.password}
                        onChange={(e) =>
                          setNewUser({ ...newUser, password: e.target.value })
                        }
                        placeholder="Enter password (min 6 characters)"
                        minLength="6"
                        required
                        className="password-input"
                      />
                      <div className="password-buttons">
                        <button
                          type="button"
                          onClick={handleGeneratePassword}
                          className="btn btn-secondary btn-sm password-btn"
                          title="Generate secure password"
                        >
                          🎲 Generate
                        </button>
                        {newUser.password && (
                          <button
                            type="button"
                            onClick={handleCopyPassword}
                            className={`btn btn-sm password-btn ${
                              passwordCopied
                                ? "btn-success"
                                : "btn-outline-secondary"
                            }`}
                            title="Copy password to clipboard"
                          >
                            {passwordCopied ? "✓ Copied!" : "📋 Copy"}
                          </button>
                        )}
                      </div>
                    </div>
                    {newUser.password && (
                      <div className="password-strength">
                        <small className="text-muted">
                          Password strength:{" "}
                          {getPasswordStrength(newUser.password)}
                        </small>
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label>User Role</label>
                    <select
                      value={newUser.user_type}
                      onChange={(e) =>
                        setNewUser({ ...newUser, user_type: e.target.value })
                      }
                      required
                    >
                      <option value="translator">Translator</option>
                      <option value="proofreader">Proofreader</option>
                      <option value="director">Director</option>
                      <option value="actor">Actor</option>
                      <option value="operator">Operator</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={creatingUser}
                >
                  {creatingUser ? "Creating User..." : "Create User"}
                </button>
              </form>

              <h2>All Users</h2>

              {/* User Filter */}
              <div
                style={{
                  marginBottom: "1rem",
                  display: "flex",
                  gap: "1rem",
                  alignItems: "center",
                }}
              >
                <div
                  className="form-group"
                  style={{ marginBottom: 0, minWidth: "200px" }}
                >
                  <label htmlFor="userFilter">Filter by User:</label>
                  <select
                    id="userFilter"
                    value={userFilter}
                    onChange={(e) => setUserFilter(e.target.value)}
                    style={{ width: "100%" }}
                  >
                    <option value="">All Users</option>
                    {users
                      .filter((u) =>
                        ["actor", "director", "operator"].includes(u.user_type)
                      )
                      .map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name} ({user.user_type})
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <div className="users-table">
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users
                      .filter((user) => {
                        if (!userFilter) return true;
                        return user.id.toString() === userFilter;
                      })
                      .map((listedUser) => (
                        <tr key={listedUser.id}>
                          <td>{listedUser.id}</td>
                          <td>{listedUser.name}</td>
                          <td>{listedUser.email}</td>
                          <td>
                            <span
                              className={`role-badge role-${listedUser.user_type}`}
                            >
                              {listedUser.user_type}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`status-badge ${
                                listedUser.is_active
                                  ? "status-active"
                                  : "status-inactive"
                              }`}
                            >
                              {listedUser.is_active ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td>
                            {new Date(
                              listedUser.created_at
                            ).toLocaleDateString()}
                          </td>
                          <td>
                            <div style={{ display: "flex", gap: "0.5rem" }}>
                              <button
                                className="btn btn-outline-warning btn-sm"
                                onClick={async () => {
                                  try {
                                    const apiUrl = await getApiUrl(
                                      `/api/admin/users/${listedUser.id}/reset-password`
                                    );
                                    const resp = await axios.post(apiUrl, {});
                                    alert(
                                      `Temporary password for ${listedUser.email}:\n\n${resp.data.tempPassword}`
                                    );
                                  } catch (e) {
                                    alert(
                                      "Failed to reset password: " +
                                        (e.response?.data?.message || e.message)
                                    );
                                  }
                                }}
                                disabled={user && listedUser.id === user.id}
                                title="Reset password (user must change on next login)"
                              >
                                🔐 Reset
                              </button>
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => showDeleteConfirm(listedUser)}
                                disabled={user && listedUser.id === user.id}
                                title={
                                  user && listedUser.id === user.id
                                    ? "Cannot delete your own account"
                                    : "Delete user"
                                }
                              >
                                🗑️ Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              {deleteConfirm.show && deleteConfirm.user && (
                <div
                  className="modal-overlay"
                  style={{
                    position: "fixed",
                    inset: 0,
                    background: "rgba(0,0,0,0.3)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1000,
                  }}
                >
                  <div
                    className="modal"
                    style={{
                      background: "#fff",
                      padding: "1rem",
                      borderRadius: "8px",
                      width: "min(420px, 90vw)",
                    }}
                  >
                    <h3 style={{ marginTop: 0 }}>Delete User</h3>
                    <p>
                      Are you sure you want to delete{" "}
                      <strong>{deleteConfirm.user.name}</strong> (
                      {deleteConfirm.user.email})? This will remove their
                      related data.
                    </p>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: "0.5rem",
                        marginTop: "1rem",
                      }}
                    >
                      <button
                        className="btn btn-secondary"
                        onClick={cancelUserDelete}
                        disabled={deletingUser}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={handleDeleteUser}
                        disabled={deletingUser}
                      >
                        {deletingUser ? "Deleting…" : "Confirm Delete"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "notifications" && (
            <div className="admin-section">
              <h2>Notifications</h2>
              <div className="notifications-list">
                {notifications.length === 0 ? (
                  <p>No notifications</p>
                ) : (
                  notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`notification-item ${
                        !notification.is_read ? "unread" : ""
                      }`}
                      onClick={() =>
                        !notification.is_read &&
                        markNotificationRead(notification.id)
                      }
                    >
                      <div className="notification-header">
                        <span className="notification-title">
                          {notification.title}
                        </span>
                        <span className="notification-time">
                          {new Date(notification.created_at).toLocaleString()}
                        </span>
                      </div>
                      <div className="notification-message">
                        {notification.message}
                      </div>
                      {notification.project_id && (
                        <div className="notification-project">
                          Project ID: {notification.project_id}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === "email" && (
            <div className="admin-section">
              <h2>Email Configuration</h2>

              {emailConfigError && (
                <div
                  className="alert alert-danger"
                  style={{
                    marginBottom: "1rem",
                    padding: "1rem",
                    backgroundColor: "#f8d7da",
                    border: "1px solid #f5c6cb",
                    borderRadius: "5px",
                    color: "#721c24",
                  }}
                >
                  <strong>⚠️ Error:</strong> {emailConfigError}
                </div>
              )}

              {emailConfigSuccess && (
                <div
                  className="alert alert-success"
                  style={{
                    marginBottom: "1rem",
                    padding: "1rem",
                    backgroundColor: "#d4edda",
                    border: "1px solid #c3e6cb",
                    borderRadius: "5px",
                    color: "#155724",
                  }}
                >
                  <strong>✅ Success:</strong> {emailConfigSuccess}
                </div>
              )}

              <form onSubmit={saveEmailConfig} className="admin-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="smtp_host">SMTP Host:</label>
                    <input
                      type="text"
                      id="smtp_host"
                      value={emailConfig.smtp_host}
                      onChange={(e) =>
                        setEmailConfig({
                          ...emailConfig,
                          smtp_host: e.target.value,
                        })
                      }
                      placeholder="smtp.gmail.com"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="smtp_port">SMTP Port:</label>
                    <input
                      type="number"
                      id="smtp_port"
                      value={emailConfig.smtp_port}
                      onChange={(e) =>
                        setEmailConfig({
                          ...emailConfig,
                          smtp_port: parseInt(e.target.value),
                        })
                      }
                      placeholder="587"
                      min="1"
                      max="65535"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="smtp_user">SMTP Username (Email):</label>
                    <input
                      type="email"
                      id="smtp_user"
                      value={emailConfig.smtp_user}
                      onChange={(e) =>
                        setEmailConfig({
                          ...emailConfig,
                          smtp_user: e.target.value,
                        })
                      }
                      placeholder="your-email@gmail.com"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="smtp_pass">
                      SMTP Password (App Password):
                    </label>
                    <input
                      type="password"
                      id="smtp_pass"
                      value={emailConfig.smtp_pass}
                      onChange={(e) =>
                        setEmailConfig({
                          ...emailConfig,
                          smtp_pass: e.target.value,
                        })
                      }
                      placeholder="Your 16-character app password"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="admin_email">
                      Admin Email (for notifications):
                    </label>
                    <input
                      type="email"
                      id="admin_email"
                      value={emailConfig.admin_email}
                      onChange={(e) =>
                        setEmailConfig({
                          ...emailConfig,
                          admin_email: e.target.value,
                        })
                      }
                      placeholder="admin@yourdomain.com"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="frontend_url">Frontend URL:</label>
                    <input
                      type="url"
                      id="frontend_url"
                      value={emailConfig.frontend_url}
                      onChange={(e) =>
                        setEmailConfig({
                          ...emailConfig,
                          frontend_url: e.target.value,
                        })
                      }
                      placeholder="http://localhost:3000"
                      required
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={emailConfigLoading}
                  >
                    {emailConfigLoading ? "Saving..." : "Save Configuration"}
                  </button>
                </div>
              </form>

              <div
                className="email-test-section"
                style={{
                  marginTop: "2rem",
                  padding: "1.5rem",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "8px",
                }}
              >
                <h3>Test Email Configuration</h3>
                <p>
                  Send a test email to verify your configuration is working
                  correctly.
                </p>

                <div className="form-row">
                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="test_email">Test Email Address:</label>
                    <input
                      type="email"
                      id="test_email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="test@example.com"
                    />
                  </div>
                  <div
                    className="form-group"
                    style={{ display: "flex", alignItems: "end" }}
                  >
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={testEmailConfig}
                      disabled={testingEmail || !testEmail}
                      style={{ marginLeft: "1rem" }}
                    >
                      {testingEmail ? "Sending..." : "Send Test Email"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "templates" && (
            <div className="admin-section">
              <h2>Email Templates Management</h2>

              {templateError && (
                <div
                  className="alert alert-danger"
                  style={{
                    marginBottom: "1rem",
                    padding: "1rem",
                    backgroundColor: "#f8d7da",
                    border: "1px solid #f5c6cb",
                    borderRadius: "5px",
                    color: "#721c24",
                  }}
                >
                  <strong>⚠️ Error:</strong> {templateError}
                </div>
              )}

              {templateSuccess && (
                <div
                  className="alert alert-success"
                  style={{
                    marginBottom: "1rem",
                    padding: "1rem",
                    backgroundColor: "#d4edda",
                    border: "1px solid #c3e6cb",
                    borderRadius: "5px",
                    color: "#155724",
                  }}
                >
                  <strong>✅ Success:</strong> {templateSuccess}
                </div>
              )}

              <div style={{ display: "flex", gap: "2rem", marginTop: "1rem" }}>
                <div style={{ flex: "1", minWidth: "300px" }}>
                  <h3>Available Templates</h3>
                  <div
                    style={{
                      border: "1px solid #ddd",
                      borderRadius: "5px",
                      padding: "1rem",
                    }}
                  >
                    {emailTemplates.map((template) => (
                      <div
                        key={template.id}
                        style={{
                          padding: "0.75rem",
                          border: "1px solid #eee",
                          borderRadius: "5px",
                          marginBottom: "0.5rem",
                          cursor: "pointer",
                          backgroundColor:
                            selectedTemplate?.id === template.id
                              ? "#e7f3ff"
                              : "white",
                          borderColor:
                            selectedTemplate?.id === template.id
                              ? "#007bff"
                              : "#eee",
                        }}
                        onClick={() => selectTemplate(template.template_type)}
                      >
                        <h4 style={{ margin: "0 0 0.5rem 0", color: "#333" }}>
                          {template.template_type
                            .replace("_", " ")
                            .replace(/\b\w/g, (l) => l.toUpperCase())}
                        </h4>
                        <p
                          style={{
                            margin: "0",
                            fontSize: "0.9em",
                            color: "#666",
                          }}
                        >
                          {template.subject}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ flex: "2", minWidth: "500px" }}>
                  {selectedTemplate ? (
                    <div>
                      <h3>
                        Edit Template:{" "}
                        {selectedTemplate.template_type
                          .replace("_", " ")
                          .replace(/\b\w/g, (l) => l.toUpperCase())}
                      </h3>

                      <div style={{ marginBottom: "1rem" }}>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "0.5rem",
                            fontWeight: "bold",
                          }}
                        >
                          Email Subject:
                        </label>
                        <input
                          type="text"
                          value={selectedTemplate.subject}
                          onChange={(e) =>
                            setSelectedTemplate({
                              ...selectedTemplate,
                              subject: e.target.value,
                            })
                          }
                          style={{
                            width: "100%",
                            padding: "0.5rem",
                            border: "1px solid #ddd",
                            borderRadius: "3px",
                          }}
                          placeholder="Email subject with variables like {{project_name}}"
                        />
                      </div>

                      <div style={{ marginBottom: "1rem" }}>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "0.5rem",
                            fontWeight: "bold",
                          }}
                        >
                          HTML Content:
                        </label>
                        <textarea
                          value={selectedTemplate.html_content}
                          onChange={(e) =>
                            setSelectedTemplate({
                              ...selectedTemplate,
                              html_content: e.target.value,
                            })
                          }
                          style={{
                            width: "100%",
                            height: "300px",
                            padding: "0.5rem",
                            border: "1px solid #ddd",
                            borderRadius: "3px",
                            fontFamily: "monospace",
                          }}
                          placeholder="HTML email content with variables like {{user_name}}"
                        />
                      </div>

                      <div style={{ marginBottom: "1rem" }}>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "0.5rem",
                            fontWeight: "bold",
                          }}
                        >
                          Text Content (Optional):
                        </label>
                        <textarea
                          value={selectedTemplate.text_content || ""}
                          onChange={(e) =>
                            setSelectedTemplate({
                              ...selectedTemplate,
                              text_content: e.target.value,
                            })
                          }
                          style={{
                            width: "100%",
                            height: "150px",
                            padding: "0.5rem",
                            border: "1px solid #ddd",
                            borderRadius: "3px",
                          }}
                          placeholder="Plain text version of the email"
                        />
                      </div>

                      <div
                        style={{
                          display: "flex",
                          gap: "1rem",
                          marginBottom: "1rem",
                        }}
                      >
                        <button
                          onClick={saveTemplate}
                          disabled={templateLoading}
                          style={{
                            padding: "0.5rem 1rem",
                            backgroundColor: "var(--moshi-dark)",
                            color: "var(--moshi-orange)",
                            border: "1px solid var(--moshi-dark)",
                            borderRadius: "3px",
                            cursor: templateLoading ? "not-allowed" : "pointer",
                          }}
                        >
                          {templateLoading ? "Saving..." : "Save Template"}
                        </button>

                        <button
                          onClick={previewTemplate}
                          disabled={templateLoading}
                          style={{
                            padding: "0.5rem 1rem",
                            backgroundColor: "var(--moshi-gray)",
                            color: "var(--moshi-orange)",
                            border: "1px solid var(--moshi-gray)",
                            borderRadius: "3px",
                            cursor: templateLoading ? "not-allowed" : "pointer",
                          }}
                        >
                          Preview
                        </button>
                      </div>

                      <div
                        style={{
                          marginBottom: "1rem",
                          padding: "1rem",
                          backgroundColor: "#f8f9fa",
                          borderRadius: "5px",
                        }}
                      >
                        <h4>Test Email</h4>
                        <div
                          style={{
                            display: "flex",
                            gap: "1rem",
                            alignItems: "end",
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <label
                              style={{
                                display: "block",
                                marginBottom: "0.5rem",
                              }}
                            >
                              Test Email Address:
                            </label>
                            <input
                              type="email"
                              value={testEmail}
                              onChange={(e) => setTestEmail(e.target.value)}
                              style={{
                                width: "100%",
                                padding: "0.5rem",
                                border: "1px solid #ddd",
                                borderRadius: "3px",
                              }}
                              placeholder="test@example.com"
                            />
                          </div>
                          <button
                            onClick={testTemplate}
                            disabled={templateLoading || !testEmail}
                            style={{
                              padding: "0.5rem 1rem",
                              backgroundColor: "var(--moshi-dark)",
                              color: "var(--moshi-orange)",
                              border: "1px solid var(--moshi-dark)",
                              borderRadius: "3px",
                              cursor:
                                templateLoading || !testEmail
                                  ? "not-allowed"
                                  : "pointer",
                            }}
                          >
                            Send Test
                          </button>
                        </div>
                      </div>

                      <div style={{ marginBottom: "1rem" }}>
                        <h4>Available Variables</h4>
                        <div
                          style={{
                            maxHeight: "200px",
                            overflowY: "auto",
                            border: "1px solid #ddd",
                            borderRadius: "3px",
                            padding: "1rem",
                          }}
                        >
                          {templateVariables.map((variable, index) => (
                            <div
                              key={index}
                              style={{
                                marginBottom: "0.5rem",
                                padding: "0.5rem",
                                backgroundColor: "#f8f9fa",
                                borderRadius: "3px",
                              }}
                            >
                              <code
                                style={{
                                  backgroundColor: "#e9ecef",
                                  padding: "0.2rem 0.4rem",
                                  borderRadius: "3px",
                                }}
                              >
                                {variable.name}
                              </code>
                              <p
                                style={{
                                  margin: "0.25rem 0 0 0",
                                  fontSize: "0.9em",
                                  color: "#666",
                                }}
                              >
                                {variable.description}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "2rem",
                        color: "#666",
                      }}
                    >
                      <h3>Select a template to edit</h3>
                      <p>
                        Choose a template from the list on the left to start
                        editing.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Preview Modal */}
              {previewData && (
                <div
                  style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0,0,0,0.5)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1000,
                  }}
                >
                  <div
                    style={{
                      backgroundColor: "white",
                      padding: "2rem",
                      borderRadius: "8px",
                      maxWidth: "80%",
                      maxHeight: "80%",
                      overflow: "auto",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "1rem",
                      }}
                    >
                      <h3>Email Preview</h3>
                      <button
                        onClick={() => setPreviewData(null)}
                        style={{
                          padding: "0.5rem 1rem",
                          backgroundColor: "#dc3545",
                          color: "white",
                          border: "none",
                          borderRadius: "3px",
                          cursor: "pointer",
                        }}
                      >
                        Close
                      </button>
                    </div>

                    <div style={{ marginBottom: "1rem" }}>
                      <strong>Subject:</strong> {previewData.subject}
                    </div>

                    <div
                      style={{
                        border: "1px solid #ddd",
                        borderRadius: "5px",
                        padding: "1rem",
                        backgroundColor: "#f8f9fa",
                        maxHeight: "400px",
                        overflow: "auto",
                      }}
                      dangerouslySetInnerHTML={{
                        __html: previewData.html_content,
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "time-tracking" && <AdminTimeTracking user={user} />}

          {activeTab === "project-view" && (
            <div className="admin-section">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1rem",
                }}
              >
                <h2>Project View: {selectedProject?.name || "Loading..."}</h2>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setActiveTab("projects")}
                >
                  ← Back to Projects
                </button>
              </div>

              {projectViewLoading ? (
                <div style={{ textAlign: "center", padding: "2rem" }}>
                  Loading project details...
                </div>
              ) : selectedProject ? (
                <div>
                  {/* Project Info */}
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "1rem",
                      borderRadius: "8px",
                      marginBottom: "2rem",
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(200px, 1fr))",
                        gap: "1rem",
                      }}
                    >
                      <div>
                        <strong>Project ID:</strong> {selectedProject.id}
                      </div>
                      <div>
                        <strong>Languages:</strong>{" "}
                        {selectedProject.source_language} →{" "}
                        {selectedProject.target_language}
                      </div>
                      <div>
                        <strong>Status:</strong>
                        <span
                          className={`status-badge status-${selectedProject.status}`}
                          style={{ marginLeft: "0.5rem" }}
                        >
                          {selectedProject.status}
                        </span>
                      </div>
                      <div>
                        <strong>Current User:</strong>{" "}
                        {selectedProject.current_user_name || "Unassigned"}
                      </div>
                      <div>
                        <strong>CPS/CPL Limits:</strong>{" "}
                        {selectedProject.cps_limit}/{selectedProject.cpl_limit}
                      </div>
                      <div>
                        <strong>Created:</strong>{" "}
                        {new Date(
                          selectedProject.created_at
                        ).toLocaleDateString()}
                      </div>
                    </div>
                  </div>

                  {/* Translation Progress */}
                  <div
                    style={{
                      backgroundColor: "#e3f2fd",
                      padding: "1rem",
                      borderRadius: "8px",
                      marginBottom: "2rem",
                    }}
                  >
                    <h3>Translation Progress</h3>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(150px, 1fr))",
                        gap: "1rem",
                        marginTop: "1rem",
                      }}
                    >
                      <div style={{ textAlign: "center" }}>
                        <div
                          style={{
                            fontSize: "2rem",
                            fontWeight: "bold",
                            color: "#1976d2",
                          }}
                        >
                          {projectEntries.length}
                        </div>
                        <div>Total Entries</div>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <div
                          style={{
                            fontSize: "2rem",
                            fontWeight: "bold",
                            color: "#2e7d32",
                          }}
                        >
                          {
                            projectEntries.filter(
                              (e) => e.target_text && e.target_text.trim()
                            ).length
                          }
                        </div>
                        <div>Translated</div>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <div
                          style={{
                            fontSize: "2rem",
                            fontWeight: "bold",
                            color: "#f57c00",
                          }}
                        >
                          {
                            projectEntries.filter(
                              (e) => !e.target_text || !e.target_text.trim()
                            ).length
                          }
                        </div>
                        <div>Pending</div>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <div
                          style={{
                            fontSize: "2rem",
                            fontWeight: "bold",
                            color: "#7b1fa2",
                          }}
                        >
                          {Math.round(
                            (projectEntries.filter(
                              (e) => e.target_text && e.target_text.trim()
                            ).length /
                              projectEntries.length) *
                              100
                          ) || 0}
                          %
                        </div>
                        <div>Complete</div>
                      </div>
                    </div>
                  </div>

                  {/* Subtitle Entries */}
                  <h3>Subtitle Entries</h3>
                  <div
                    style={{
                      maxHeight: "600px",
                      overflowY: "auto",
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                    }}
                  >
                    {projectEntries.length === 0 ? (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "2rem",
                          color: "#6c757d",
                        }}
                      >
                        No subtitle entries found
                      </div>
                    ) : (
                      projectEntries.map((entry, index) => (
                        <div
                          key={entry.id || index}
                          style={{
                            padding: "1rem",
                            borderBottom: "1px solid #eee",
                            backgroundColor:
                              entry.target_text && entry.target_text.trim()
                                ? "#f8fff8"
                                : "#fff8f8",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              marginBottom: "0.5rem",
                            }}
                          >
                            <div
                              style={{ fontSize: "0.875rem", color: "#6c757d" }}
                            >
                              Entry #{entry.sequence_number || index + 1} |{" "}
                              {entry.start_time} → {entry.end_time}
                            </div>
                            <div style={{ fontSize: "0.875rem" }}>
                              {entry.target_text && entry.target_text.trim() ? (
                                <span
                                  style={{
                                    color: "#2e7d32",
                                    fontWeight: "bold",
                                  }}
                                >
                                  ✅ Translated
                                </span>
                              ) : (
                                <span
                                  style={{
                                    color: "#f57c00",
                                    fontWeight: "bold",
                                  }}
                                >
                                  ⏳ Pending
                                </span>
                              )}
                            </div>
                          </div>

                          <div style={{ marginBottom: "0.5rem" }}>
                            <strong>Source:</strong>
                            <div
                              style={{
                                padding: "0.5rem",
                                backgroundColor: "#f8f9fa",
                                borderRadius: "4px",
                                marginTop: "0.25rem",
                                fontStyle: "italic",
                              }}
                            >
                              {entry.source_text}
                            </div>
                          </div>

                          <div>
                            <strong>Translation:</strong>
                            <div
                              style={{
                                padding: "0.5rem",
                                backgroundColor:
                                  entry.target_text && entry.target_text.trim()
                                    ? "#e8f5e8"
                                    : "#fff3e0",
                                borderRadius: "4px",
                                marginTop: "0.25rem",
                                minHeight: "2rem",
                              }}
                            >
                              {entry.target_text && entry.target_text.trim() ? (
                                entry.target_text
                              ) : (
                                <span
                                  style={{
                                    color: "#6c757d",
                                    fontStyle: "italic",
                                  }}
                                >
                                  No translation yet
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    padding: "2rem",
                    color: "#6c757d",
                  }}
                >
                  No project selected
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        /* Regular User Content */
        <>
          {projects.length === 0 ? (
            <div
              className="card"
              style={{ textAlign: "center", padding: "3rem" }}
            >
              <h3>No projects available</h3>
              <p style={{ color: "#6c757d", marginTop: "1rem" }}>
                There are currently no projects available for your role (
                {user.user_type}).
              </p>
            </div>
          ) : (
            <div className="projects-grid">
              {projects.map((project) => (
                <div key={project.id} className="project-card">
                  <h3 className="project-title">{project.name}</h3>

                  <div className="project-info">
                    <div className="project-languages">
                      <span className="language-badge">
                        {project.source_language}
                      </span>
                      <span>→</span>
                      <span className="language-badge">
                        {project.target_language}
                      </span>
                    </div>

                    <div>
                      <span
                        className={`project-status ${getStatusClass(
                          project.status
                        )}`}
                      >
                        {getStatusText(project.status)}
                      </span>
                    </div>

                    {project.current_user_name && (
                      <div style={{ fontSize: "0.875rem", color: "#6c757d" }}>
                        Currently working: {project.current_user_name}
                      </div>
                    )}
                  </div>

                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    {project.current_user_id === user.id ||
                    !project.current_user_id ? (
                      <button
                        className="btn btn-primary"
                        onClick={() => handleStartProject(project.id)}
                        style={{ flex: 1 }}
                      >
                        {project.current_user_id === user.id
                          ? "Continue"
                          : "Start"}
                      </button>
                    ) : (
                      <button
                        className="btn btn-secondary"
                        disabled
                        style={{ flex: 1 }}
                      >
                        In Use
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Error Reporting Modal */}
      {showErrorReport && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "2rem",
              borderRadius: "8px",
              maxWidth: "500px",
              width: "90%",
              maxHeight: "80vh",
              overflow: "auto",
            }}
          >
            <h3 style={{ marginBottom: "1rem", color: "#dc3545" }}>
              🚩 Report Issue to Admin
            </h3>

            {reportSuccess ? (
              <div style={{ textAlign: "center", padding: "1rem" }}>
                <div
                  style={{
                    color: "#28a745",
                    fontSize: "2rem",
                    marginBottom: "1rem",
                  }}
                >
                  ✅
                </div>
                <p style={{ color: "#28a745", fontWeight: "bold" }}>
                  Issue reported successfully! Admin will be notified.
                </p>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: "1rem" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      fontWeight: "bold",
                    }}
                  >
                    Describe the issue:
                  </label>
                  <textarea
                    value={errorMessage}
                    onChange={(e) => setErrorMessage(e.target.value)}
                    placeholder="Please describe the issue you're experiencing..."
                    style={{
                      width: "100%",
                      minHeight: "120px",
                      padding: "0.75rem",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      resize: "vertical",
                    }}
                  />
                </div>

                <div
                  style={{
                    backgroundColor: "#f8f9fa",
                    padding: "1rem",
                    borderRadius: "4px",
                    marginBottom: "1rem",
                    fontSize: "0.875rem",
                    color: "#6c757d",
                  }}
                >
                  <strong>Report will include:</strong>
                  <ul style={{ margin: "0.5rem 0 0 1rem" }}>
                    <li>Your name: {user.name}</li>
                    <li>Your email: {user.email}</li>
                    <li>Your role: {user.user_type}</li>
                    <li>Your message: {errorMessage || "(empty)"}</li>
                  </ul>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: "0.5rem",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowErrorReport(false);
                      setErrorMessage("");
                      setError("");
                    }}
                    disabled={sendingReport}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-warning"
                    onClick={handleSendErrorReport}
                    disabled={sendingReport || !errorMessage.trim()}
                  >
                    {sendingReport ? "⏳ Sending..." : "📧 Send Report"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* File Explorer Modal */}
      {showExplorerModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "2rem",
              borderRadius: "8px",
              maxWidth: "90%",
              maxHeight: "90%",
              overflow: "auto",
              minWidth: "600px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "1rem",
              }}
            >
              <h2>📁 NAS File Explorer</h2>
              <button
                onClick={() => setShowExplorerModal(false)}
                style={{
                  padding: "0.5rem 1rem",
                  backgroundColor: "#dc3545",
                  color: "white",
                  border: "none",
                  borderRadius: "3px",
                  cursor: "pointer",
                }}
              >
                ✕ Close
              </button>
            </div>

            {/* Navigation Bar */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "1rem",
                padding: "0.5rem",
                backgroundColor: "#f8f9fa",
                border: "1px solid #dee2e6",
                borderRadius: "4px",
              }}
            >
              <button
                type="button"
                onClick={navigateBack}
                disabled={directoryHistory.length <= 1}
                className="btn btn-sm btn-secondary"
                style={{ marginRight: "0.5rem" }}
              >
                ← Back
              </button>
              <span
                style={{
                  fontFamily: "monospace",
                  fontSize: "0.9rem",
                  color: "#495057",
                  fontWeight: "bold",
                }}
              >
                {currentDirectory}
              </span>
            </div>

            {/* File List */}
            <div
              style={{
                maxHeight: "400px",
                overflowY: "auto",
                border: "1px solid #dee2e6",
                borderRadius: "4px",
                backgroundColor: "#fff",
                marginBottom: "1rem",
              }}
            >
              {loadingExplorer ? (
                <div style={{ padding: "2rem", textAlign: "center" }}>
                  <div>Loading files...</div>
                </div>
              ) : (
                <div>
                  {explorerFiles.map((file, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "0.75rem",
                        borderBottom: "1px solid #eee",
                        cursor: file.isdir ? "pointer" : "default",
                        backgroundColor: file.isdir ? "#f8f9fa" : "#fff",
                        transition: "background-color 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = file.isdir
                          ? "#e9ecef"
                          : "#f8f9fa";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = file.isdir
                          ? "#f8f9fa"
                          : "#fff";
                      }}
                      onClick={() => {
                        if (file.isdir) {
                          navigateToDirectory(file.path);
                        } else {
                          selectExplorerFile(file);
                        }
                      }}
                    >
                      <span
                        style={{
                          marginRight: "0.75rem",
                          fontSize: "1.5rem",
                        }}
                      >
                        {file.isdir
                          ? "📁"
                          : file.type === "video"
                          ? "🎬"
                          : file.type === "subtitle"
                          ? "📝"
                          : "📄"}
                      </span>
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontWeight: file.isdir ? "bold" : "normal",
                            fontSize: "1rem",
                          }}
                        >
                          {file.name}
                        </div>
                        {!file.isdir && (
                          <div style={{ fontSize: "0.8rem", color: "#6c757d" }}>
                            {Math.round(file.size / 1024)} KB
                          </div>
                        )}
                      </div>
                      {!file.isdir && (
                        <div style={{ marginLeft: "0.5rem" }}>
                          {selectedExplorerFiles.video?.path === file.path && (
                            <span
                              style={{
                                color: "#28a745",
                                fontSize: "0.9rem",
                                fontWeight: "bold",
                                backgroundColor: "#d4edda",
                                padding: "0.25rem 0.5rem",
                                borderRadius: "3px",
                              }}
                            >
                              ✓ Video Selected
                            </span>
                          )}
                          {selectedExplorerFiles.subtitle?.path ===
                            file.path && (
                            <span
                              style={{
                                color: "#007bff",
                                fontSize: "0.9rem",
                                fontWeight: "bold",
                                backgroundColor: "#d1ecf1",
                                padding: "0.25rem 0.5rem",
                                borderRadius: "3px",
                              }}
                            >
                              ✓ Subtitle Selected
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  {explorerFiles.length === 0 && (
                    <div
                      style={{
                        padding: "2rem",
                        textAlign: "center",
                        color: "#6c757d",
                      }}
                    >
                      No files found in this directory
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Selected Files Summary */}
            <div
              style={{
                padding: "1rem",
                backgroundColor: "#e9ecef",
                borderRadius: "4px",
                marginBottom: "1rem",
              }}
            >
              <div
                style={{
                  fontSize: "1rem",
                  fontWeight: "bold",
                  marginBottom: "0.5rem",
                }}
              >
                Selected Files:
              </div>
              <div style={{ fontSize: "0.9rem" }}>
                <div>
                  🎬 Video:{" "}
                  {selectedExplorerFiles.video?.name || "None selected"}
                </div>
                <div>
                  📝 Subtitle:{" "}
                  {selectedExplorerFiles.subtitle?.name || "None selected"}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: "flex", gap: "1rem" }}>
              <button
                type="button"
                onClick={handleExplorerFileSelection}
                className="btn btn-primary"
                disabled={
                  !selectedExplorerFiles.video ||
                  !selectedExplorerFiles.subtitle
                }
                style={{ flex: 1 }}
              >
                Select Files
              </button>
              <button
                type="button"
                onClick={() => setShowExplorerModal(false)}
                className="btn btn-secondary"
                style={{ flex: 1 }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Project Modal */}
      {showEditModal && editingProject && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "2rem",
              borderRadius: "8px",
              maxWidth: "600px",
              width: "90%",
              maxHeight: "80vh",
              overflow: "auto",
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: "1.5rem" }}>
              ✏️ Edit Project: {editingProject.name}
            </h3>

            <form onSubmit={handleUpdateProject}>
              <div className="form-row">
                <div className="form-group">
                  <label>Project Name</label>
                  <input
                    type="text"
                    value={editingProjectData.name}
                    onChange={(e) =>
                      setEditingProjectData({
                        ...editingProjectData,
                        name: e.target.value,
                      })
                    }
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Source Language</label>
                  <input
                    type="text"
                    value={editingProjectData.source_language}
                    onChange={(e) =>
                      setEditingProjectData({
                        ...editingProjectData,
                        source_language: e.target.value,
                      })
                    }
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Target Language</label>
                  <input
                    type="text"
                    value={editingProjectData.target_language}
                    onChange={(e) =>
                      setEditingProjectData({
                        ...editingProjectData,
                        target_language: e.target.value,
                      })
                    }
                    required
                  />
                </div>
                <div className="form-group">
                  <label>CPS Limit (Characters Per Second)</label>
                  <input
                    type="number"
                    value={editingProjectData.cps_limit}
                    onChange={(e) =>
                      setEditingProjectData({
                        ...editingProjectData,
                        cps_limit: parseInt(e.target.value) || 20,
                      })
                    }
                    min="1"
                    max="50"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>CPL Limit (Characters Per Line)</label>
                  <input
                    type="number"
                    value={editingProjectData.cpl_limit}
                    onChange={(e) =>
                      setEditingProjectData({
                        ...editingProjectData,
                        cpl_limit: parseInt(e.target.value) || 42,
                      })
                    }
                    min="1"
                    max="100"
                    required
                  />
                </div>
                <div className="form-group">
                  {/* Empty div for layout balance */}
                </div>
              </div>

              {/* File Selection Section */}
              <div
                style={{
                  marginTop: "2rem",
                  padding: "1rem",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "8px",
                }}
              >
                <h4 style={{ marginTop: 0, marginBottom: "1rem" }}>
                  📁 Update Files
                </h4>

                {/* Current Files Display */}
                <div
                  style={{
                    marginBottom: "1rem",
                    padding: "0.5rem",
                    backgroundColor: "#e9ecef",
                    borderRadius: "4px",
                  }}
                >
                  <strong>Current Files:</strong>
                  <div style={{ fontSize: "0.9rem", marginTop: "0.25rem" }}>
                    <div>
                      🎬 Video: {editingProjectData.video_path || "Not set"}
                    </div>
                    <div>
                      📝 Subtitle:{" "}
                      {editingProjectData.source_subtitle_path || "Not set"}
                    </div>
                  </div>
                </div>

                {/* Upload Files */}
                {!editingUseNasFiles && (
                  <form
                    onSubmit={handleEditingFileUpload}
                    style={{ marginBottom: "1rem" }}
                  >
                    <div className="form-row">
                      <div className="form-group">
                        <label>New Video File (MP4)</label>
                        <input
                          type="file"
                          accept="video/mp4"
                          onChange={(e) =>
                            setEditingUploadedFiles({
                              ...editingUploadedFiles,
                              video: e.target.files[0],
                            })
                          }
                        />
                      </div>
                      <div className="form-group">
                        <label>New Subtitle File (SRT)</label>
                        <input
                          type="file"
                          accept=".srt"
                          onChange={(e) =>
                            setEditingUploadedFiles({
                              ...editingUploadedFiles,
                              subtitle: e.target.files[0],
                            })
                          }
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      className="btn btn-primary btn-sm"
                      disabled={
                        uploading ||
                        !editingUploadedFiles.video ||
                        !editingUploadedFiles.subtitle
                      }
                    >
                      {uploading ? "Uploading..." : "Upload New Files"}
                    </button>
                  </form>
                )}

                {/* NAS File Selection */}
                {editingUseNasFiles && (
                  <div style={{ marginBottom: "1rem" }}>
                    <button
                      type="button"
                      onClick={() => setShowEditingExplorerModal(true)}
                      className="btn btn-primary btn-sm"
                      style={{ marginBottom: "0.5rem" }}
                    >
                      📁 Open NAS File Explorer
                    </button>
                  </div>
                )}
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "1rem",
                  justifyContent: "flex-end",
                  marginTop: "1.5rem",
                }}
              >
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelEdit}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Update Project
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Project Confirmation Modal */}
      {showDeleteProjectModal && projectToDelete && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "2rem",
              borderRadius: "8px",
              maxWidth: "500px",
              width: "90%",
            }}
          >
            <h3 style={{ marginTop: 0, color: "#dc3545" }}>
              🗑️ Delete Project
            </h3>
            <p>
              Are you sure you want to delete the project{" "}
              <strong>"{projectToDelete.name}"</strong>?
            </p>
            <p style={{ color: "#dc3545", fontWeight: "bold" }}>
              ⚠️ This action cannot be undone and will remove all project data
              including translations!
            </p>
            <div
              style={{
                display: "flex",
                gap: "1rem",
                justifyContent: "flex-end",
                marginTop: "1.5rem",
              }}
            >
              <button className="btn btn-secondary" onClick={cancelDelete}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={confirmDeleteProject}>
                Delete Project
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Editing NAS File Explorer Modal */}
      {showEditingExplorerModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "2rem",
              borderRadius: "8px",
              maxWidth: "90%",
              maxHeight: "90%",
              overflow: "auto",
              minWidth: "600px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "1rem",
              }}
            >
              <h2>📁 NAS File Explorer - Edit Project</h2>
              <button
                onClick={() => setShowEditingExplorerModal(false)}
                style={{
                  padding: "0.5rem 1rem",
                  backgroundColor: "#dc3545",
                  color: "white",
                  border: "none",
                  borderRadius: "3px",
                  cursor: "pointer",
                }}
              >
                ✕ Close
              </button>
            </div>

            {/* Navigation Bar */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "1rem",
                padding: "0.5rem",
                backgroundColor: "#f8f9fa",
                border: "1px solid #dee2e6",
                borderRadius: "4px",
              }}
            >
              <button
                type="button"
                onClick={navigateBack}
                disabled={directoryHistory.length <= 1}
                className="btn btn-sm btn-secondary"
                style={{ marginRight: "0.5rem" }}
              >
                ← Back
              </button>
              <span
                style={{
                  fontFamily: "monospace",
                  fontSize: "0.9rem",
                  color: "#495057",
                  fontWeight: "bold",
                }}
              >
                {currentDirectory}
              </span>
            </div>

            {/* File List */}
            <div
              style={{
                maxHeight: "400px",
                overflowY: "auto",
                border: "1px solid #dee2e6",
                borderRadius: "4px",
                backgroundColor: "#fff",
                marginBottom: "1rem",
              }}
            >
              {loadingExplorer ? (
                <div style={{ padding: "2rem", textAlign: "center" }}>
                  <div>Loading files...</div>
                </div>
              ) : (
                <div>
                  {explorerFiles.map((file, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "0.75rem",
                        borderBottom: "1px solid #eee",
                        cursor: file.isdir ? "pointer" : "default",
                        backgroundColor: file.isdir ? "#f8f9fa" : "#fff",
                        transition: "background-color 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = file.isdir
                          ? "#e9ecef"
                          : "#f8f9fa";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = file.isdir
                          ? "#f8f9fa"
                          : "#fff";
                      }}
                      onClick={() => {
                        if (file.isdir) {
                          navigateToDirectory(file.path);
                        } else {
                          if (file.type === "video") {
                            setEditingSelectedExplorerFiles((prev) => ({
                              ...prev,
                              video: file,
                            }));
                          } else if (file.type === "subtitle") {
                            setEditingSelectedExplorerFiles((prev) => ({
                              ...prev,
                              subtitle: file,
                            }));
                          }
                        }
                      }}
                    >
                      <span
                        style={{
                          marginRight: "0.75rem",
                          fontSize: "1.5rem",
                        }}
                      >
                        {file.isdir
                          ? "📁"
                          : file.type === "video"
                          ? "🎬"
                          : file.type === "subtitle"
                          ? "📝"
                          : "📄"}
                      </span>
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontWeight: file.isdir ? "bold" : "normal",
                            fontSize: "1rem",
                          }}
                        >
                          {file.name}
                        </div>
                        {!file.isdir && (
                          <div style={{ fontSize: "0.8rem", color: "#6c757d" }}>
                            {Math.round(file.size / 1024)} KB
                          </div>
                        )}
                      </div>
                      {!file.isdir && (
                        <div style={{ marginLeft: "0.5rem" }}>
                          {editingSelectedExplorerFiles.video?.path ===
                            file.path && (
                            <span
                              style={{
                                color: "#28a745",
                                fontSize: "0.9rem",
                                fontWeight: "bold",
                                backgroundColor: "#d4edda",
                                padding: "0.25rem 0.5rem",
                                borderRadius: "3px",
                              }}
                            >
                              ✓ Video Selected
                            </span>
                          )}
                          {editingSelectedExplorerFiles.subtitle?.path ===
                            file.path && (
                            <span
                              style={{
                                color: "#007bff",
                                fontSize: "0.9rem",
                                fontWeight: "bold",
                                backgroundColor: "#d1ecf1",
                                padding: "0.25rem 0.5rem",
                                borderRadius: "3px",
                              }}
                            >
                              ✓ Subtitle Selected
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  {explorerFiles.length === 0 && (
                    <div
                      style={{
                        padding: "2rem",
                        textAlign: "center",
                        color: "#6c757d",
                      }}
                    >
                      No files found in this directory
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Selected Files Summary */}
            <div
              style={{
                padding: "1rem",
                backgroundColor: "#e9ecef",
                borderRadius: "4px",
                marginBottom: "1rem",
              }}
            >
              <div
                style={{
                  fontSize: "1rem",
                  fontWeight: "bold",
                  marginBottom: "0.5rem",
                }}
              >
                Selected Files for Editing:
              </div>
              <div style={{ fontSize: "0.9rem" }}>
                <div>
                  🎬 Video:{" "}
                  {editingSelectedExplorerFiles.video?.name || "None selected"}
                </div>
                <div>
                  📝 Subtitle:{" "}
                  {editingSelectedExplorerFiles.subtitle?.name ||
                    "None selected"}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: "flex", gap: "1rem" }}>
              <button
                type="button"
                onClick={handleEditingNasFileSelection}
                className="btn btn-primary"
                disabled={
                  !editingSelectedExplorerFiles.video ||
                  !editingSelectedExplorerFiles.subtitle
                }
                style={{ flex: 1 }}
              >
                Select Files
              </button>
              <button
                type="button"
                onClick={() => setShowEditingExplorerModal(false)}
                className="btn btn-secondary"
                style={{ flex: 1 }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
